import React from 'react';
import { AnnualPeriodHeadLabel } from './AnnualPeriodHeadLabel';
import type { ClassScheduleRow } from '../../services/sectionLayoutService';
import type { AnnualWeekdayKey } from '../../types/domain';
import { addDays, parseISODate, toISODate } from '../../utils/date';

export type AnnualPreviewWeekRow = {
  mondayIso: string;
  weekIndex: number;
  monthLabel: string;
  dateRangeLabel: string;
  datesByWeekday: string[];
};

function normalizeScheduleTimeForTooltip(raw: string | undefined): string {
  const t = (raw ?? '').trim();
  if (!t) return '00:00';
  const m = t.match(/^(\d{1,2})\s*:\s*(\d{2})$/);
  if (!m) return t;
  const h = Math.min(23, Math.max(0, parseInt(m[1], 10) || 0));
  const min = Math.min(59, Math.max(0, parseInt(m[2], 10) || 0));
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function formatPeriodHeaderTimeRange(row: ClassScheduleRow | undefined): string {
  const start = normalizeScheduleTimeForTooltip(row?.startTime);
  const end = normalizeScheduleTimeForTooltip(row?.endTime);
  return `${start} - ${end}`;
}

/** 연간시간표 본문 표와 동일한 thead·셀 클래스로 주·교시 그리드만 렌더 (데이터 비움) */
export function createAnnualShellWeekRows(
  count: number,
  showSaturday: boolean,
  startMondayIso = '2026-03-02'
): AnnualPreviewWeekRow[] {
  const mon0 = parseISODate(startMondayIso);
  if (Number.isNaN(mon0.getTime())) return [];
  const rows: AnnualPreviewWeekRow[] = [];
  for (let i = 0; i < count; i += 1) {
    const mon = addDays(mon0, i * 7);
    const mondayIso = toISODate(mon);
    const datesByWeekday: string[] = [];
    for (let d = 0; d < 6; d += 1) {
      datesByWeekday.push(toISODate(addDays(mon, d)));
    }
    const d0 = parseISODate(datesByWeekday[0] ?? mondayIso);
    const d4 = parseISODate(datesByWeekday[4] ?? mondayIso);
    const dateRangeLabel = showSaturday
      ? `${String(d0.getMonth() + 1).padStart(2, '0')}.${String(d0.getDate()).padStart(2, '0')}-${String(
          parseISODate(datesByWeekday[5] ?? mondayIso).getMonth() + 1
        ).padStart(2, '0')}.${String(parseISODate(datesByWeekday[5] ?? mondayIso).getDate()).padStart(2, '0')}`
      : `${String(d0.getMonth() + 1).padStart(2, '0')}.${String(d0.getDate()).padStart(2, '0')}-${String(
          d4.getMonth() + 1
        ).padStart(2, '0')}.${String(d4.getDate()).padStart(2, '0')}`;
    rows.push({
      mondayIso,
      weekIndex: i + 1,
      monthLabel: String(mon.getMonth() + 1),
      dateRangeLabel,
      datesByWeekday,
    });
  }
  return rows;
}

export type AnnualTimetablePreviewMainTableProps = {
  termKey: '1' | '2';
  anchorGradeId: string;
  weekdayKeysToShow: ReadonlyArray<{ key: AnnualWeekdayKey; label: string }>;
  periodLabels: string[];
  sectionRows: ClassScheduleRow[];
  weeks: AnnualPreviewWeekRow[];
  termHoursCell: string;
  termDaysCell?: string;
  selectedHeadDateLabel?: string;
};

export const AnnualTimetablePreviewMainTable: React.FC<AnnualTimetablePreviewMainTableProps> = ({
  termKey,
  anchorGradeId,
  weekdayKeysToShow,
  periodLabels,
  sectionRows,
  weeks,
  termHoursCell,
  termDaysCell = '',
  selectedHeadDateLabel = '월   일',
}) => {
  const periodsPerDay = periodLabels.length;
  const headLabel = selectedHeadDateLabel;

  return (
    <div className="cp-annual-timetable-table-wrap">
      <table
        className={
          'cp-weekly-schedule-table cp-annual-timetable-fixed-table ' +
          (termKey === '1' ? 'cp-annual-term-table-term1' : 'cp-annual-term-table-term2')
        }
      >
        <thead>
          <tr>
            <th className="cp-weekly-schedule-th-month">월</th>
            <th className="cp-weekly-schedule-th-week">주</th>
            <th className="cp-weekly-schedule-th-daterange">일자</th>
            {weekdayKeysToShow.map(({ key, label }) => (
              <th key={key} colSpan={periodsPerDay} className="cp-weekly-schedule-th-weekday">
                {label}
              </th>
            ))}
            <th className="cp-weekly-schedule-th-hours">
              수업
              <br />
              시수
            </th>
            <th className="cp-weekly-schedule-th-days">
              수업
              <br />
              일수
            </th>
          </tr>
          <tr className="cp-annual-timetable-period-head-row">
            <th
              colSpan={3}
              className="cp-weekly-schedule-th-daterange cp-annual-selected-date-headcell"
              data-cp-annual-date-head-anchor={`${anchorGradeId}-${termKey}`}
            >
              <button type="button" className="cp-annual-selected-date-headcell-btn" disabled>
                {headLabel}
              </button>
            </th>
            {weekdayKeysToShow.map(({ key }) =>
              periodLabels.map((label, p) => {
                const scheduleRow = sectionRows[p];
                const timeRangeTip = formatPeriodHeaderTimeRange(scheduleRow);
                return (
                  <th key={`${key}-${p}`} className="cp-weekly-schedule-th-period cp-annual-period-head-th">
                    <AnnualPeriodHeadLabel label={label} />
                    <span className="cp-annual-period-head-th-tooltip" role="tooltip">
                      <span className="cp-annual-period-head-th-tooltip-inner">{timeRangeTip}</span>
                    </span>
                  </th>
                );
              })
            )}
            <th className="cp-weekly-schedule-th-hours cp-weekly-schedule-semester-hours">{termHoursCell}</th>
            <th className="cp-weekly-schedule-th-days cp-weekly-schedule-semester-days">{termDaysCell}</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => {
            const dateRangeDisplay = week.dateRangeLabel;
            return (
              <tr key={week.mondayIso}>
                <td className="cp-weekly-schedule-td-month">{week.monthLabel}</td>
                <td className="cp-weekly-schedule-td-week">{week.weekIndex}</td>
                <td className="cp-weekly-schedule-td-daterange cp-annual-week-memo-anchor-cell">{dateRangeDisplay}</td>
                {weekdayKeysToShow.map(({ key: dayKey }) =>
                  periodLabels.map((_, p) => (
                    <td key={`${dayKey}-${p}`} className="cp-weekly-schedule-cell">
                      <div className="cp-base-timetable-cell-inner cp-annual-timetable-cell-inner">
                        <span className="cp-base-timetable-empty"> </span>
                      </div>
                    </td>
                  ))
                )}
                <td className="cp-weekly-schedule-td-hours" />
                <td className="cp-weekly-schedule-td-days" />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
